import { useMemo, useState } from 'react'

import { useFacilitySearch } from '@/hooks/useFacilitySearch'
import { useTranslation } from 'react-i18next'
import { useDebounce } from 'use-debounce'

import type { MultiSelectOption } from '@hivewatch/dark'

import {
  getFacilityOptionsWithMatchedByAddressCue,
  getIncidentFacilityOptionsWithAddress,
} from '@/utils/forms/optionBuilders'
import { SEARCH_DEBOUNCE_TIME } from '@/utils/forms/validation'

export const useUsersAccessFacilityOptions = () => {
  const { t } = useTranslation()
  const [facilitySearch, setFacilitySearch] = useState('')
  const [debouncedFacilitySearch] = useDebounce(
    facilitySearch,
    SEARCH_DEBOUNCE_TIME
  )
  const normalizedSearch = debouncedFacilitySearch.trim()
  const {
    facilitiesData,
    isFacilitiesLoading: isSearchLoading,
    isError: isFacilitiesError,
  } = useFacilitySearch({ searchQuery: normalizedSearch })

  const facilityOptions: MultiSelectOption[] = useMemo(
    () =>
      getFacilityOptionsWithMatchedByAddressCue(
        getIncidentFacilityOptionsWithAddress(
          facilitiesData,
          true,
          normalizedSearch
        ),
        t('common:facility_matched_by_address')
      ),
    [facilitiesData, normalizedSearch, t]
  )

  return {
    isFacilitiesError,
    facilityOptions,
    // Below MIN_CHAR_COUNT, useFacilitySearch returns undefined until the
    // browse-mode query resolves; once anything has loaded (browse page or a
    // search result), don't drop back to a loading state on every keystroke.
    isFacilitiesLoading: isSearchLoading && !facilitiesData,
    setFacilitySearch,
  }
}
